import {
  ListingNeighborhoodOption,
  loadNeighborhoodsForCity,
} from 'api/services/filterService'
import {
  isCanadianPostalCodeQuery,
  searchCitiesForFilter,
} from 'api/services/nominatimService'
import classNames from 'classnames'
import { useCallback, useEffect, useRef, useState, KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { ParsedOsmAddress } from 'types/osmAddress'

export type FilterLocationValue = {
  city: string
  zip_code: string
  neighborhoods: string[]
}

interface FilterLocationInputsProps {
  city: string
  zipCode?: string
  neighborhoods: string[]
  onChange: (value: FilterLocationValue) => void
}

function suggestionPrimaryLabel(item: ParsedOsmAddress): string {
  return item.city || item.street || item.formattedAddress
}

function suggestionSecondaryLabel(item: ParsedOsmAddress): string {
  return [item.neighborhood, item.postalCode, item.province].filter(Boolean).join(' · ')
}

const FilterLocationInputs = ({
  city,
  zipCode = '',
  neighborhoods,
  onChange,
}: FilterLocationInputsProps) => {
  const { t } = useTranslation()
  const [cityQuery, setCityQuery] = useState(city)
  const [suggestions, setSuggestions] = useState<ParsedOsmAddress[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const [searchError, setSearchError] = useState<string | null>(null)
  const [neighborhoodOptions, setNeighborhoodOptions] = useState<
    ListingNeighborhoodOption[]
  >([])
  const [isLoadingNeighborhoods, setIsLoadingNeighborhoods] = useState(false)

  const containerRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const searchRequestIdRef = useRef(0)
  const selectedCityRef = useRef<string | null>(city || null)
  const neighborhoodsRequestIdRef = useRef(0)

  useEffect(() => {
    setCityQuery(city)
    selectedCityRef.current = city || null
  }, [city])

  const runCitySearch = useCallback(
    async (query: string, requestId: number) => {
      const trimmed = query.trim()
      if (trimmed.length < 2) {
        if (requestId === searchRequestIdRef.current) {
          setSuggestions([])
          setIsSearching(false)
        }
        return
      }

      setIsSearching(true)
      setSearchError(null)

      try {
        const results = await searchCitiesForFilter(trimmed, { limit: 6 })
        if (requestId !== searchRequestIdRef.current) return
        setSuggestions(results)
      } catch {
        if (requestId !== searchRequestIdRef.current) return
        setSuggestions([])
        setSearchError(t('filters.citySearchError'))
      } finally {
        if (requestId === searchRequestIdRef.current) {
          setIsSearching(false)
        }
      }
    },
    [t]
  )

  useEffect(() => {
    if (selectedCityRef.current === cityQuery) return

    if (debounceRef.current) clearTimeout(debounceRef.current)
    const requestId = ++searchRequestIdRef.current
    debounceRef.current = setTimeout(() => {
      void runCitySearch(cityQuery, requestId)
    }, 400)

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [cityQuery, runCitySearch])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    const trimmedCity = city.trim()
    if (trimmedCity.length < 2) {
      setNeighborhoodOptions([])
      setIsLoadingNeighborhoods(false)
      return
    }

    const requestId = ++neighborhoodsRequestIdRef.current
    setIsLoadingNeighborhoods(true)

    void loadNeighborhoodsForCity(trimmedCity)
      .then(options => {
        if (requestId !== neighborhoodsRequestIdRef.current) return
        setNeighborhoodOptions(options)
      })
      .catch(() => {
        if (requestId !== neighborhoodsRequestIdRef.current) return
        setNeighborhoodOptions([])
      })
      .finally(() => {
        if (requestId !== neighborhoodsRequestIdRef.current) return
        setIsLoadingNeighborhoods(false)
      })
  }, [city])

  const closeSuggestions = () => {
    searchRequestIdRef.current += 1
    if (debounceRef.current) clearTimeout(debounceRef.current)
    setSuggestions([])
    setIsOpen(false)
    setIsSearching(false)
    setSearchError(null)
  }

  const applyCitySelection = (nextCity: string, nextZip = '') => {
    const normalizedCity = nextCity.trim()
    selectedCityRef.current = normalizedCity || null
    setCityQuery(normalizedCity)
    closeSuggestions()
    onChange({
      city: normalizedCity,
      zip_code: nextZip,
      neighborhoods: [],
    })
  }

  const handleSelectSuggestion = (address: ParsedOsmAddress) => {
    const nextCity = address.city || suggestionPrimaryLabel(address)
    const nextZip =
      address.postalCode ||
      (isCanadianPostalCodeQuery(cityQuery) ? cityQuery.trim().toUpperCase() : '')
    applyCitySelection(nextCity, nextZip)
  }

  const handleCityInputChange = (value: string) => {
    const hadCommittedCity = Boolean(city.trim())
    selectedCityRef.current = null
    setCityQuery(value)
    setIsOpen(true)
    setSearchError(null)

    const trimmed = value.trim()
    if (!trimmed) {
      // Explicit clear — remove city filter.
      onChange({ city: '', zip_code: '', neighborhoods: [] })
      setSuggestions([])
      return
    }

    // Typing only drives autocomplete. Do not apply the listings filter until
    // a suggestion is chosen. Clear any previously committed city so results
    // are not stuck on the old selection while the user searches.
    if (hadCommittedCity) {
      onChange({ city: '', zip_code: '', neighborhoods: [] })
    }
  }

  const handleCityBlur = () => {
    // If nothing was selected, revert the input to the committed city value.
    window.setTimeout(() => {
      if (selectedCityRef.current) return
      setCityQuery(city)
      closeSuggestions()
    }, 150)
  }

  const handleCityKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      setCityQuery(city)
      selectedCityRef.current = city.trim() || null
      closeSuggestions()
      return
    }
    if (e.key === 'Enter' && suggestions.length > 0) {
      e.preventDefault()
      handleSelectSuggestion(suggestions[0])
    }
  }

  const clearCityField = () => {
    selectedCityRef.current = null
    setCityQuery('')
    closeSuggestions()
    onChange({ city: '', zip_code: '', neighborhoods: [] })
  }

  const toggleNeighborhood = (name: string) => {
    const selected = neighborhoods.includes(name)
      ? neighborhoods.filter(item => item !== name)
      : [...neighborhoods, name]
    onChange({
      city: city.trim(),
      zip_code: zipCode,
      neighborhoods: selected,
    })
  }

  const removeNeighborhood = (name: string) => {
    onChange({
      city: city.trim(),
      zip_code: zipCode,
      neighborhoods: neighborhoods.filter(item => item !== name),
    })
  }

  const showSuggestions = isOpen && suggestions.length > 0
  const cityReady = city.trim().length >= 2

  return (
    <div className="flex flex-col gap-3">
      <div ref={containerRef} className="relative">
        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
          {t('filters.cityLabel')}
        </label>
        <div className="relative">
          <input
            type="text"
            autoComplete="off"
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={showSuggestions}
            aria-controls="city-filter-suggestions"
            placeholder={t('filters.cityPlaceholder')}
            value={cityQuery}
            onChange={e => handleCityInputChange(e.target.value)}
            onKeyDown={handleCityKeyDown}
            onBlur={handleCityBlur}
            onFocus={() => {
              if (suggestions.length > 0 && selectedCityRef.current !== cityQuery.trim()) {
                setIsOpen(true)
              }
            }}
            className={classNames(
              'w-full rounded-lg border border-gray-300 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-imovPrimary focus:outline-none focus:ring-1 focus:ring-imovPrimary',
              cityQuery ? 'pl-3 pr-10' : 'px-3'
            )}
          />
          {cityQuery ? (
            <button
              type="button"
              onMouseDown={e => e.preventDefault()}
              onClick={clearCityField}
              aria-label={t('filters.clearCity')}
              className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 transition-colors hover:text-gray-700"
            >
              <span aria-hidden className="flex h-5 w-5 items-center justify-center rounded-full bg-gray-200 text-sm leading-none text-gray-600 hover:bg-gray-300">
                ×
              </span>
            </button>
          ) : null}
        </div>

        {isSearching && isOpen ? (
          <p className="absolute left-0 top-full z-20 mt-1 text-xs text-gray-500">
            {t('filters.citySearching')}
          </p>
        ) : null}

        {searchError && isOpen ? (
          <p className="absolute left-0 top-full z-20 mt-1 text-xs text-red-600" role="alert">
            {searchError}
          </p>
        ) : null}

        {showSuggestions ? (
          <ul
            id="city-filter-suggestions"
            role="listbox"
            className="absolute z-30 mt-1 max-h-60 w-full overflow-y-auto rounded-xl border border-gray-200 bg-white py-1 shadow-lg"
          >
            {suggestions.map((item, index) => (
              <li key={`${item.formattedAddress}-${index}`}>
                <button
                  type="button"
                  role="option"
                  onMouseDown={e => e.preventDefault()}
                  onClick={() => handleSelectSuggestion(item)}
                  className="block w-full px-3 py-2.5 text-left text-sm text-gray-800 hover:bg-imovPrimary/5"
                >
                  <span className="block font-medium">{suggestionPrimaryLabel(item)}</span>
                  {suggestionSecondaryLabel(item) ? (
                    <span className="block text-xs text-gray-500">
                      {suggestionSecondaryLabel(item)}
                    </span>
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      {cityReady ? (
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
            {t('filters.neighborhoodLabel')}
          </label>

          {isLoadingNeighborhoods ? (
            <p className="text-xs text-gray-500">{t('filters.neighborhoodLoading')}</p>
          ) : neighborhoodOptions.length === 0 ? (
            <p className="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-500">
              {t('filters.neighborhoodEmpty')}
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              {neighborhoods.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {neighborhoods.map(name => (
                    <button
                      key={`selected-${name}`}
                      type="button"
                      onClick={() => removeNeighborhood(name)}
                      className="inline-flex items-center gap-1 rounded-full bg-imovPrimary/10 px-2.5 py-1 text-xs font-medium text-imovPrimary"
                    >
                      {name}
                      <span aria-hidden>×</span>
                    </button>
                  ))}
                </div>
              ) : null}

              <div
                className="space-y-0.5 rounded-lg border border-gray-200 p-1.5"
                role="group"
                aria-label={t('filters.neighborhoodLabel')}
              >
                {neighborhoodOptions.map(option => {
                  const selected = neighborhoods.includes(option.neighborhood)
                  const inputId = `neighborhood-${option.neighborhood}`
                  return (
                    <label
                      key={option.neighborhood}
                      htmlFor={inputId}
                      className={classNames(
                        'flex w-full cursor-pointer items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm transition-colors',
                        selected
                          ? 'bg-imovPrimary/10 text-imovPrimary'
                          : 'text-gray-800 hover:bg-gray-50'
                      )}
                    >
                      <input
                        id={inputId}
                        type="checkbox"
                        checked={selected}
                        onChange={() => toggleNeighborhood(option.neighborhood)}
                        className="h-4 w-4 shrink-0 rounded border-gray-300 text-imovPrimary focus:ring-imovPrimary"
                      />
                      <span className="min-w-0 flex-1 truncate font-medium">
                        {option.neighborhood}
                      </span>
                      <span className="shrink-0 text-xs text-gray-500">
                        {t(
                          option.listingCount === 1
                            ? 'filters.neighborhoodCount_one'
                            : 'filters.neighborhoodCount_other',
                          { count: option.listingCount }
                        )}
                      </span>
                    </label>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      ) : null}
    </div>
  )
}

export default FilterLocationInputs
